import { useEffect, useRef, useState } from "react";
import { supabase } from "../utils/supabaseClient";

export const CATEGORY_OPTIONS = [
  "Grains",
  "Legumes",
  "Spices & Seasonings",
  "Fish",
  "Meat",
  "Vegetables",
  "Fruits",
  "Drinks",
  "Snacks",
  "Frozen",
  "Bakery",
  "Other",
];

const MAX_IMAGE_MB = 5;

const EMPTY_FORM = {
  name: "",
  description: "",
  price: "",
  category: "",
  stock: 0,
  image_url: "",
  country: "UK",
};

const stockOf = (p) => Number(p.stock ?? 0);

const fromProduct = (p) => ({
  name: p.name ?? "",
  description: p.description ?? "",
  price: p.price ?? "",
  category: p.category ?? "",
  stock: p.stock ?? 0,
  image_url: p.image_url ?? "",
  country: p.country ?? "UK",
});

// Add mode:  <ProductForm onSaved={...} />
// Edit mode: <ProductForm product={theProduct} onSaved={...} onCancel={...} />
// onSaved receives { mode: "added" | "updated", name }
export default function ProductForm({ product = null, onSaved, onCancel, className = "" }) {
  const isEditing = Boolean(product);

  const [form, setForm] = useState(product ? fromProduct(product) : EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  // Switching to a different product (or back to "add") loads it into the form
  useEffect(() => {
    setForm(product ? fromProduct(product) : EMPTY_FORM);
    setImageFile(null);
    setFormError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [product]);

  // Local preview of a newly chosen image file
  useEffect(() => {
    if (!imageFile) {
      setPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(imageFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  // An older product may have a category that is not in the list: keep it selectable
  const categoryOptions =
    form.category && !CATEGORY_OPTIONS.includes(form.category)
      ? [form.category, ...CATEGORY_OPTIONS]
      : CATEGORY_OPTIONS;

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setFormError("Please choose an image file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      setFormError(`The image must be smaller than ${MAX_IMAGE_MB} MB.`);
      e.target.value = "";
      return;
    }
    setFormError("");
    setImageFile(file);
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setFormError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");

    const name = form.name.trim();
    const price = Number(form.price);
    const stock = Number(form.stock);

    if (!name) return setFormError("Enter a product name.");
    if (!Number.isFinite(price) || price <= 0) {
      return setFormError("Enter a price greater than 0.");
    }
    if (!isEditing && !form.category) {
      return setFormError("Choose a category so customers can find the product.");
    }
    if (!Number.isInteger(stock) || stock < 0) {
      return setFormError("Stock must be a whole number, 0 or more.");
    }

    setSaving(true);
    try {
      let imageUrl = form.image_url || null;

      if (imageFile) {
        const ext = imageFile.name.split(".").pop();
        const filePath = `products/${Date.now()}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from("product-images")
          .upload(filePath, imageFile);
        if (uploadError) throw uploadError;

        const { data: publicUrlData } = supabase.storage
          .from("product-images")
          .getPublicUrl(filePath);
        imageUrl = publicUrlData.publicUrl;
      }

      const payload = {
        name,
        description: form.description.trim(),
        price,
        category: form.category || null,
        country: form.country || "UK",
        stock,
        image_url: imageUrl,
      };

      if (isEditing) {
        // If the stock box wasn't changed, leave stock out of the update so a
        // sale that happened while this form was open isn't overwritten.
        if (stock === stockOf(product)) delete payload.stock;

        const { error } = await supabase
          .from("products")
          .update(payload)
          .eq("id", product.id);
        if (error) throw error;

        onSaved?.({ mode: "updated", name });
      } else {
        const { error } = await supabase
          .from("products")
          .insert({ ...payload, low_stock_threshold: 5 });
        if (error) throw error;

        resetForm();
        onSaved?.({ mode: "added", name });
      }
    } catch (err) {
      console.error("Saving product failed:", err);
      setFormError(`Could not save the product: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  const imageToShow = preview || form.image_url;

  return (
    <form className={`adm-card ${className}`.trim()} onSubmit={handleSubmit} noValidate>
      <div className="adm-card-head">
        <h3>{isEditing ? "Edit product" : "Add a product"}</h3>
      </div>

      <div className="adm-card-body">
        {formError && (
          <p className="ui-alert ui-alert--error" role="alert">
            {formError}
          </p>
        )}

        <div className="ui-field">
          <label className="ui-label" htmlFor="p-name">Name</label>
          <input id="p-name" name="name" className="ui-input" value={form.name} onChange={handleChange} />
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="p-desc">Description</label>
          <textarea id="p-desc" name="description" className="ui-textarea" value={form.description} onChange={handleChange} />
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="p-price">Price (£)</label>
          <input id="p-price" name="price" type="number" min="0" step="0.01" className="ui-input" value={form.price} onChange={handleChange} />
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="p-cat">Category</label>
          <select id="p-cat" name="category" className="ui-select" value={form.category} onChange={handleChange}>
            <option value="">Select a category</option>
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="p-country">Sold in</label>
          <select id="p-country" name="country" className="ui-select" value={form.country} onChange={handleChange}>
            <option value="UK">United Kingdom</option>
            <option value="Nigeria">Nigeria</option>
          </select>
          <p className="ui-hint">Customers only see products for the country flag they select.</p>
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="p-stock">Stock level</label>
          <input id="p-stock" name="stock" type="number" min="0" step="1" className="ui-input" value={form.stock} onChange={handleChange} />
          {isEditing && (
            <p className="ui-hint">
              Saving sets stock to this number. Leave it as it is to keep the live stock figure.
            </p>
          )}
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="p-image">Product image</label>
          {imageToShow && <img className="adm-image-preview" src={imageToShow} alt="Product preview" />}
          <input id="p-image" ref={fileInputRef} type="file" accept="image/*" className="ui-input" onChange={handleFile} />
        </div>

        <div className="adm-form-actions">
          <button type="submit" className="ui-btn ui-btn--primary" disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Save changes" : "Add product"}
          </button>
          {isEditing && onCancel && (
            <button type="button" className="ui-btn ui-btn--ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </form>
  );
}